/**
 * Activity Log filter constants and helpers.
 */

export const ACTIVITY_LOG_DEFAULT_LOOKBACK_DAYS = 30;

export const DATE_PRESETS = [
  { id: '7d', label: 'Last 7d', days: 7 },
  { id: '15d', label: 'Last 15d', days: 15 },
  { id: '30d', label: 'Last 30d', days: 30 },
  { id: '90d', label: 'Last 90d', days: 90 },
];

export const ACTIVITY_TYPE_OPTIONS = [
  { id: 'complaint_created', label: 'Complaint Opened' },
  { id: 'complaint_closed', label: 'Complaint Closed' },
  { id: 'complaint_cancelled', label: 'Complaint Cancelled' },
  { id: 'complaint_escalated', label: 'Complaint Escalated', locationOnly: true },
  { id: 'maintenance_logged', label: 'Maintenance Logged' },
  { id: 'device_added', label: 'Device Added' },
];

export const ACTIVITY_CATEGORY_PRESETS = [
  { id: 'all', label: 'All' },
  { id: 'complaints', label: 'Complaints Only' },
  { id: 'maintenance', label: 'Maintenance Only' },
  { id: 'devices', label: 'Devices Added' },
];

export const MAINTENANCE_TYPE_OPTIONS = [
  { id: 'ppm', label: 'PPM' },
  { id: 'calibration', label: 'Calibration' },
  { id: 'compliance', label: 'Compliance' },
];

export const SEVERITY_OPTIONS = [
  { id: 'minor', label: 'Minor' },
  { id: 'major', label: 'Major' },
  { id: 'critical', label: 'Critical' },
];

export const COMPLAINT_STATUS_OPTIONS = [
  { id: 'closed', label: 'Closed' },
  { id: 'cancelled', label: 'Cancelled' },
];

export const COMPLAINT_ACTIVITY_TYPES = [
  'complaint_created',
  'complaint_closed',
  'complaint_cancelled',
  'complaint_escalated',
];

export const MAINTENANCE_ACTIVITY_TYPE = 'maintenance_logged';
export const DEVICE_ADDED_ACTIVITY_TYPE = 'device_added';

export const COMPLAINT_STATUS_TO_ACTIVITY_TYPE = {
  closed: 'complaint_closed',
  cancelled: 'complaint_cancelled',
};

export const ACTIVITY_LOG_FILTER_HINTS = {
  bannerLead: 'Choose either Maintenance Type or Complaint filters (Status/Severity).',
  bannerBody:
    'Activity Types further narrow which events are shown. Maintenance Type applies to maintenance logs; Status and Severity apply to complaints and can be combined.',
  maintenanceTypeDisabled: '(disabled — Complaint filters active)',
  complaintFiltersDisabled: '(disabled — Maintenance filters active)',
  activityTypeDisabledMaintenance: '(disabled — Maintenance filters active)',
  activityTypeDisabledComplaint: '(disabled — Complaint filters active)',
  exclusiveGroupDisabledDevice: '(disabled — Device activity selected)',
};

export const hasComplaintAdvancedFilters = (filters) =>
  (filters?.complaintStatusFilters?.length || 0) > 0
  || (filters?.severityFilters?.length || 0) > 0;

export const hasMaintenanceAdvancedFilters = (filters) =>
  (filters?.maintenanceTypeFilters?.length || 0) > 0;

export const isDeviceOnlyActivitySelection = (activityTypeFilters) =>
  Array.isArray(activityTypeFilters)
  && activityTypeFilters.length > 0
  && activityTypeFilters.every((id) => id === DEVICE_ADDED_ACTIVITY_TYPE);

export const compatibleActivityTypeFilters = (filters, selectedTypes = []) => {
  if (hasMaintenanceAdvancedFilters(filters)) {
    return selectedTypes.filter((id) => id === MAINTENANCE_ACTIVITY_TYPE);
  }
  if (hasComplaintAdvancedFilters(filters)) {
    return selectedTypes.filter((id) => COMPLAINT_ACTIVITY_TYPES.includes(id));
  }
  return selectedTypes;
};

export const isActivityTypeOptionDisabled = (typeId, filters) => {
  if (hasMaintenanceAdvancedFilters(filters)) {
    return typeId !== MAINTENANCE_ACTIVITY_TYPE;
  }
  if (hasComplaintAdvancedFilters(filters)) {
    return !COMPLAINT_ACTIVITY_TYPES.includes(typeId);
  }
  return false;
};

export const toDateString = (date) => date.toISOString().split('T')[0];

export const daysAgoDateString = (days) => {
  const today = new Date();
  return toDateString(new Date(today.getTime() - days * 24 * 60 * 60 * 1000));
};

export const isLocationScopedRole = (role) => role === 'manager' || role === 'admin';

export const getActivityTypeLabel = (typeId) =>
  ACTIVITY_TYPE_OPTIONS.find((opt) => opt.id === typeId)?.label || typeId || '';

export const getActivityTypeOptionsForRole = (role) =>
  ACTIVITY_TYPE_OPTIONS.filter((opt) => {
    if (opt.locationOnly && !isLocationScopedRole(role)) return false;
    return true;
  });

export const getCategoryPresetsForRole = (role) =>
  ACTIVITY_CATEGORY_PRESETS.filter((opt) => {
    if (opt.locationOnly && !isLocationScopedRole(role)) return false;
    return true;
  });

export const categoryToActivityTypes = (categoryId, role) => {
  switch (categoryId) {
    case 'complaints':
      return isLocationScopedRole(role)
        ? ['complaint_created', 'complaint_closed', 'complaint_cancelled', 'complaint_escalated']
        : ['complaint_created', 'complaint_closed', 'complaint_cancelled'];
    case 'maintenance':
      return ['maintenance_logged'];
    case 'devices':
      return ['device_added'];
    default:
      return [];
  }
};

/** Empty dates → backend last-30-days window. Optional presetDays is an explicit quick filter. */
export const createInitialActivityLogFilters = (presetDays = null) => {
  const hasPreset = Number.isFinite(presetDays) && presetDays > 0;
  return {
    dateFrom: hasPreset ? daysAgoDateString(presetDays) : '',
    dateTo: '',
    datePreset: hasPreset ? `${presetDays}d` : '',
    categoryPreset: 'all',
    activityTypeFilters: [],
    maintenanceTypeFilters: [],
    complaintStatusFilters: [],
    severityFilters: [],
    departmentFilters: [],
  };
};

export const isActivityLogServerDateWindowActive = (filters) =>
  !filters?.dateFrom && !filters?.dateTo;

export const isActivityLogLastNDaysActive = (filters, days) => {
  if (!filters?.dateFrom || filters.dateTo) return false;
  const presetId = `${days}d`;
  if (filters.datePreset === presetId) return true;
  return filters.dateFrom === daysAgoDateString(days);
};

export const formatActivityLogFilterDate = (dateStr) => {
  if (!dateStr) return '';
  return new Date(dateStr).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
};

/** Label for an explicit date pill only — default backend window is not a chip. */
export const getActivityLogDateFilterLabel = (filters) => {
  if (isActivityLogServerDateWindowActive(filters)) return '';
  const preset = DATE_PRESETS.find((p) => p.id === filters.datePreset);
  if (preset && !filters.dateTo) {
    return preset.label;
  }
  const from = filters.dateFrom
    ? formatActivityLogFilterDate(filters.dateFrom)
    : '…';
  const to = filters.dateTo
    ? formatActivityLogFilterDate(filters.dateTo)
    : 'today';
  return `Date: ${from} – ${to}`;
};

export const getActivityLogCategoryFilterLabel = (filters, role) => {
  if (!filters?.categoryPreset || filters.categoryPreset === 'all') return '';
  const presets = getCategoryPresetsForRole(role);
  return presets.find((p) => p.id === filters.categoryPreset)?.label || '';
};

/** User-applied chips only. Search, "All", and the backend last-30-days window are not chips. */
export const hasActivityLogActiveFilters = (filters) =>
  Boolean(
    filters.dateFrom
    || filters.dateTo
    || filters.datePreset
    || (filters.categoryPreset && filters.categoryPreset !== 'all')
    || filters.activityTypeFilters?.length
    || filters.maintenanceTypeFilters?.length
    || filters.complaintStatusFilters?.length
    || filters.severityFilters?.length
    || filters.departmentFilters?.length
  );

export const isActivityLogDatePresetActive = (filters, presetId) =>
  filters.datePreset === presetId;

export const buildActivityLogApiParams = ({
  filters,
  searchTerm,
  page,
  perPage,
  sorting,
}) => {
  const params = {
    page,
    limit: perPage,
  };

  if (searchTerm?.trim()) {
    params.search = searchTerm.trim();
  }
  if (filters.dateFrom) {
    params.date_from = filters.dateFrom;
  }
  if (filters.dateTo) {
    params.date_to = filters.dateTo;
  }

  const activityTypes = [...(filters.activityTypeFilters || [])];
  if (filters.categoryPreset && filters.categoryPreset !== 'all' && activityTypes.length === 0) {
    // category preset handled separately in page via activity_types
  }
  if (activityTypes.length > 0) {
    params.activity_types = activityTypes.join(',');
  }

  if (filters.maintenanceTypeFilters?.length > 0) {
    params.maintenance_types = filters.maintenanceTypeFilters.join(',');
  }
  if (filters.complaintStatusFilters?.length > 0) {
    params.complaint_statuses = filters.complaintStatusFilters.join(',');
  }
  if (filters.severityFilters?.length > 0) {
    params.severities = filters.severityFilters.join(',');
  }
  if (filters.departmentFilters?.length > 0) {
    params.departments = filters.departmentFilters.join(',');
  }
  if (sorting?.sort_by) {
    params.sort_by = sorting.sort_by;
  }
  if (sorting?.sort_direction) {
    params.sort_direction = sorting.sort_direction;
  }
  return params;
};
